import React, { useEffect, useMemo } from 'react';

import classnames from 'classnames';

import HorizontalScroller from 'components/uiLibrary/HorizontalScroller';
import Typography from 'components/uiLibrary/Typography';

// TODO: FB-000000 we need to remove below dependency cycle error
// eslint-disable-next-line import/no-cycle
import FilterFacets, { FILTER_LAYOUT } from 'components/Filters/Display/FilterFacets';
import { COMPONENTS, SECTIONS } from 'components/Globals/Analytics/constants';

import { SORT_OPTION_VALUES } from 'constants/index';

import useAppContext from 'utils/hooks/useAppContext';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import useNavigate from 'utils/hooks/useNavigate';
import usePageContext from 'utils/hooks/usePageContext';
import useSelectedViewMode from 'utils/hooks/useSelectedViewMode';
import { useInfiniteQuery } from 'utils/react-query';

import queries from 'containers/Productions/queries';

import { useTranslation } from 'src/i18n';

import {
  TP,
  QUERY_PARAMS_RESERVED,
  VIEW_MODES,
  FILTER_TYPES,
  ROUTE_RESERVED_KEYWORDS,
} from 'operabase-router/constants';

import ProductionListFeed from '../ProductionListFeed';
import ProductionSortOptions from '../ProductionSortOptions';
import ProductionViewSwitcher from '../ProductionViewSwitcher';

import classes from './ProductionListView.module.scss';

const productionTrackingData = {
  section: SECTIONS.PRODUCTION_LIST_VIEW,
  component: COMPONENTS.PRODUCTION_SLUG,
};

const FeaturedPastProductions = ({ styles, limit, filters, trackingData, sort = SORT_OPTION_VALUES.FEATURED_DESC }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { entityType, entity } = usePageContext();

  const queryFn = useMemo(() => {
    const { page: _page, ...restFilters } = filters || {};

    const queryFilters = {
      ...(entityType && { entityType }),
      ...(entity?.id && { entityId: entity?.id }),
      filters: {
        ...restFilters,
        sort,
        enable_ml: restFilters?.enable_ml,
      },
      limit,
      mediaLimit: 5,
    };

    return queries.getProductions(queryFilters);
  }, [entityType, entity?.id, filters, limit, sort]);

  return (
    <div className={classes.featuredProductions}>
      <Typography variant="h2" weight="bold">
        {t(`${TP}.FN_FEATURED_PRODUCTIONS`)}
      </Typography>
      <ProductionListFeed
        viewMode={VIEW_MODES.GRID}
        queryFn={queryFn}
        sort={sort}
        limit={limit}
        trackingData={trackingData}
        styles={{
          root: styles?.feed,
          list: styles?.list,
        }}
      />
    </div>
  );
};

const ProductionListView = ({
  overrideFilters = null,
  forcedViewMode,
  forcedSortBy,
  infinite = true,
  limit,
  hideActions = false,
  showFilters = false,
  trackingData,
  styles,
}) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const navigate = useNavigate();
  const { entityType, entity } = usePageContext();
  const { obRouteContext } = useAppContext();
  const { isDesktop } = useDeviceTypeLayouts();
  const filters = overrideFilters || obRouteContext?.filterParams;
  const isRentalsListing =
    obRouteContext?.linkProps?.filters[FILTER_TYPES.RENTALS] ||
    obRouteContext?.page?.tab === ROUTE_RESERVED_KEYWORDS.rentals;

  const {
    viewMode,
    sortBy,
    isReady,
    setViewMode,
    setSortBy,
    showFeaturedPastProductions,
    featuredProductionFilterParams,
  } = useSelectedViewMode({
    filters,
    forcedViewMode,
  });

  useEffect(() => {
    if (!isReady) {
      return;
    }

    const currentViewMode = obRouteContext?.query[QUERY_PARAMS_RESERVED.VIEW_MODE];
    const currentSortBy = obRouteContext?.query[QUERY_PARAMS_RESERVED.SORT_BY];

    const shouldUpdateViewMode = viewMode && currentViewMode && viewMode !== currentViewMode;
    const shouldUpdateSortBy = sortBy && currentSortBy && sortBy !== currentSortBy;

    if (shouldUpdateViewMode || shouldUpdateSortBy) {
      navigate.updateQueryParams({
        appendParams: {
          ...(shouldUpdateViewMode && { [QUERY_PARAMS_RESERVED.VIEW_MODE]: viewMode }),
          ...(shouldUpdateSortBy && { [QUERY_PARAMS_RESERVED.SORT_BY]: sortBy }),
        },
      });
    }
    // NOTE: Only run on mount to update the query params if they are wrong
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop, isReady]);

  const queryFn = useMemo(() => {
    const { page: _page, ...restFilters } = filters || {};
    const sort = !!forcedSortBy && hideActions ? forcedSortBy : sortBy;

    const queryFilters = {
      ...(entityType && { entityType }),
      ...(entity?.id && { entityId: entity?.id }),
      filters: {
        ...restFilters,
        sort,
        enable_ml: restFilters?.enable_ml || sort === SORT_OPTION_VALUES.FEATURED_DESC,
      },
      limit,
      ...(viewMode === VIEW_MODES.GRID && {
        mediaLimit: 5,
      }),
      ...(!infinite && { page: 1 }),
    };

    if (viewMode === VIEW_MODES.DAY) {
      return queries.getPerformances(queryFilters);
    }

    return queries.getProductions(queryFilters);
  }, [entityType, entity?.id, filters, viewMode, limit, infinite, forcedSortBy, hideActions, sortBy]);

  const { data: hasResults } = useInfiniteQuery({
    ...queryFn,
    select: response => response?.pages[0]?.total > 0,
    enabled: isReady,
  });

  if (!isReady) {
    return null;
  }

  const showActions = !hideActions && hasResults && !isRentalsListing;

  return (
    <div className={classnames(classes.root, { [styles?.root]: !!styles?.root })}>
      {showActions && (
        <div className={classes.actions}>
          <div className={classes.actions_viewSwitcher}>
            <HorizontalScroller hideArrow>
              <ProductionViewSwitcher value={viewMode} setViewMode={setViewMode} />
            </HorizontalScroller>
          </div>
          <div className={classes.actions_sortOptions}>
            <ProductionSortOptions
              value={sortBy}
              setSortBy={setSortBy}
              trackingData={{
                ...productionTrackingData,
                ...trackingData,
              }}
            />
          </div>
        </div>
      )}
      {showFilters && (
        <FilterFacets
          layout={FILTER_LAYOUT.HORIZONTAL}
          styles={{
            root: classnames(classes.filterContainer, { [styles?.filterContainer]: !!styles?.filterContainer }),
          }}
          scrollable
          trackingData={{
            ...productionTrackingData,
            ...trackingData,
          }}
        />
      )}

      <div className={classes.content}>
        <ProductionListFeed
          viewMode={viewMode}
          queryFn={queryFn}
          infinite={infinite}
          trackingData={{
            ...productionTrackingData,
            ...trackingData,
          }}
          limit={limit}
          styles={{
            root: styles?.feed,
            list: styles?.list,
          }}
          {...(showFeaturedPastProductions && {
            noResultProps: {
              hideIcon: true,
              title: t(`${TP}.FN_NO_UPCOMING_PERFORMANCES`),
            },
          })}
        />
        {showFeaturedPastProductions && (
          <FeaturedPastProductions
            styles={styles}
            limit={limit}
            filters={featuredProductionFilterParams}
            trackingData={trackingData}
          />
        )}
      </div>
    </div>
  );
};

export default ProductionListView;
